/* SPEC-503 — the Docs page's pure rules: which view the reader is on, which guide answers the
 * `?` button for it, which landing page a login gets, when a guide needs re-checking, and which
 * link targets a guide may use.
 *
 * Pure, no React, no `@/` alias — asserted by `config/docs.test.ts` under Node's type
 * stripping (the same pattern as `shell.ts`).
 *
 * View ids are the registry path without its leading slash ('/' is 'overview'), plus
 * ':<tab>' for a tab: '/demand-planning?tab=amendments' → 'demand-planning:amendments'.
 * The SAME ids are written in a guide's front-matter `view_id` (scripts/build_docs_model.py
 * validates them against frontend/src/config/view-registry.json). */

export type DocsAudience = 'planner' | 'exec' | 'engineer';

export interface DocsPageMeta {
  id: string;
  title: string;
  quadrant: string;
  audience: DocsAudience;
  view_id: string | null;
  documents: string[];
}

/** The view id of a location. Nested routes (/products/28640) belong to their page. */
export function viewIdForLocation(pathname: string, search: string): string {
  const first = (pathname || '/').split('/').filter(Boolean)[0];
  const base = first ? first.toLowerCase() : 'overview';
  const tab = new URLSearchParams(search || '').get('tab');
  return tab && /^[a-z0-9_-]+$/i.test(tab) ? `${base}:${tab}` : base;
}

/** The URL the `?` button opens for a location. */
export function helpHrefFor(pathname: string, search: string): string {
  return `/docs?view=${encodeURIComponent(viewIdForLocation(pathname, search))}`;
}

export interface DocsTarget {
  page: string;
  anchor: string | null;
}

const AUDIENCE_ORDER: DocsAudience[] = ['planner', 'exec', 'engineer'];

/** The guide that answers "how does this screen work?" for a view id.
 *  1. a guide whose `view_id` is exactly this view; 2. one that lists it in `documents`;
 *  3. the same two for the page part of a tab view; 4. the screen's entry in the generated
 *  "Every screen" catalogue — so the button never lands on nothing. Among several guides the
 *  reader's own audience wins, then planner, exec, engineer. */
export function resolveView(
  pages: readonly DocsPageMeta[],
  viewId: string,
  preferred: DocsAudience,
): DocsTarget {
  const rank = (a: DocsAudience) => (a === preferred ? -1 : AUDIENCE_ORDER.indexOf(a));
  const pick = (id: string): DocsPageMeta | undefined => {
    const exact = pages.filter((p) => p.view_id === id);
    const listed = pages.filter((p) => p.view_id !== id && (p.documents || []).includes(`view:${id}`));
    for (const group of [exact, listed]) {
      if (group.length) return [...group].sort((a, b) => rank(a.audience) - rank(b.audience))[0];
    }
    return undefined;
  };
  const base = viewId.split(':')[0];
  const hit = pick(viewId) ?? (base !== viewId ? pick(base) : undefined);
  if (hit) return { page: hit.id, anchor: null };
  return { page: 'ref-pages', anchor: `view-${base}` };
}

/** Which "Getting started" a login lands on. Admin roles read the engineer guide. The
 *  working tiers (team, operations, sc_director) and an exec who can amend a forecast (the
 *  planner holds `exec` with the forecast_override capability) read the planner guide; every
 *  other login reads the exec guide. */
export function landingAudience(role: string | undefined, canAmendForecast: boolean): DocsAudience {
  if (role === 'admin' || role === 'super_admin') return 'engineer';
  if (role === 'team' || role === 'operations' || role === 'sc_director') return 'planner';
  if (role === 'exec' && canAmendForecast) return 'planner';
  return 'exec';
}

/** A guide is re-checked at least this often, even when nothing it covers has changed. */
export const REVERIFY_AFTER_DAYS = 90;

/** True when a guide's verified date is missing, unreadable, in the future or older than
 *  REVERIFY_AFTER_DAYS. The CI guard catches a guide whose screens changed; this catches
 *  one nobody has looked at for a quarter. */
export function needsReverification(verifiedAt: string | null | undefined, today: Date): boolean {
  if (!verifiedAt || !/^\d{4}-\d{2}-\d{2}$/.test(verifiedAt)) return true;
  const t = Date.parse(`${verifiedAt}T00:00:00Z`);
  if (Number.isNaN(t)) return true;
  const ageDays = (today.getTime() - t) / 86_400_000;
  return ageDays < -1 || ageDays > REVERIFY_AFTER_DAYS;
}

export type DocsLink =
  | { kind: 'doc'; page: string; anchor: string | null }
  | { kind: 'app'; to: string }
  | { kind: 'external'; href: string };

/** Classify a link inside a guide. Only three kinds render as links: another guide
 *  (`doc:<id>#<anchor>`), a screen in the app (`/path?query`), and an https URL. Anything
 *  else (javascript:, data:, protocol-relative //host, http:) renders as plain text. */
export function classifyLink(href: string): DocsLink | null {
  const h = (href || '').trim();
  const doc = /^doc:([a-z0-9][a-z0-9-]*)(?:#([a-z0-9][a-z0-9-]*))?$/.exec(h);
  if (doc) return { kind: 'doc', page: doc[1], anchor: doc[2] ?? null };
  if (h.startsWith('/') && !h.startsWith('//') && !/[\s\\]/.test(h)) return { kind: 'app', to: h };
  if (/^https:\/\/[^\s]+$/i.test(h)) return { kind: 'external', href: h };
  return null;
}

/* ── SPEC-512: nested glossary pages + the sticky panes ─────────────────────────────────── */

/** Every glossary term has its own page, id `glossary-<slug>`, served at /docs/glossary/<slug>
 *  (scripts/build_docs_model.py; a hand-written guide may not use the prefix). */
export const GLOSSARY_PAGE_ID = 'ref-glossary';
export const GLOSSARY_TERM_PREFIX = 'glossary-';
const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/** The address of a guide. A term page lives under /docs/glossary/<slug>; every other guide
 *  under /docs?page=<id>. An anchor rides as `at`. */
export function docPath(page: string, anchor?: string | null): string {
  const at = anchor ? `at=${encodeURIComponent(anchor)}` : '';
  const slug = page.startsWith(GLOSSARY_TERM_PREFIX) ? page.slice(GLOSSARY_TERM_PREFIX.length) : '';
  if (slug && SLUG_RE.test(slug)) return `/docs/glossary/${slug}${at ? `?${at}` : ''}`;
  return `/docs?page=${encodeURIComponent(page)}${at ? `&${at}` : ''}`;
}

/** The page id a /docs/glossary/<slug> address names; null for a missing or malformed slug. */
export function termPageId(slug: string | undefined): string | null {
  return slug && SLUG_RE.test(slug) ? `${GLOSSARY_TERM_PREFIX}${slug}` : null;
}

/** The tree entry to highlight for a page: a term page highlights the Glossary, its parent. */
export function treeEntryFor(pageId: string | null): string | null {
  if (!pageId) return null;
  return pageId.startsWith(GLOSSARY_TERM_PREFIX) ? GLOSSARY_PAGE_ID : pageId;
}

/** The Docs panes. The guide scrolls with the shell's main pane; the guide tree (from lg) and
 *  the "On this page" outline (from xl) stick to the top of that pane and scroll on their own,
 *  capped below the viewport height less the status bar, so a long tree never makes the page
 *  taller. `overscroll-contain` stops a pane's scroll chaining into the main pane. None of them
 *  is `fixed` or sized to the screen: the document itself never scrolls (SPEC-511). */
export const DOCS_PANES = {
  tree: 'min-w-0 lg:sticky lg:top-4 lg:self-start lg:flex lg:flex-col lg:max-h-[calc(100dvh-4rem)]',
  treeScroll: 'lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain',
  outline: 'hidden xl:block min-w-0 xl:sticky xl:top-16 xl:self-start xl:max-h-[calc(100dvh-7rem)] xl:overflow-y-auto xl:overscroll-contain',
} as const;

export interface GlossaryTermEntry {
  id: string;
  term: string;
  topic: string;
  letter: string;
}

export interface GlossaryTopic {
  id: string;
  title: string;
  count: number;
}

/** The letter chips: A–Z then '#', each with how many terms start with it. A letter with no
 *  term is still listed (count 0) so the row never shifts; the page disables it. */
export function glossaryLetters(terms: readonly GlossaryTermEntry[]): { letter: string; count: number }[] {
  const letters = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'];
  return letters.map((letter) => ({ letter, count: terms.filter((t) => t.letter === letter).length }));
}

/** The topic sections for a chosen letter (null = all). Each term appears once, under its own
 *  topic, in the order given; a topic the filter empties is dropped. A term whose topic is not
 *  listed is shown under 'Other' rather than lost. */
export function glossarySections(
  topics: readonly GlossaryTopic[],
  terms: readonly GlossaryTermEntry[],
  letter: string | null,
): { topic: GlossaryTopic; terms: GlossaryTermEntry[] }[] {
  const known = new Set(topics.map((t) => t.id));
  const shown = letter ? terms.filter((t) => t.letter === letter) : terms;
  const all = known.has('other') ? [...topics] : [...topics, { id: 'other', title: 'Other', count: 0 }];
  return all
    .map((topic) => ({
      topic,
      terms: shown.filter((t) => (known.has(t.topic) ? t.topic : 'other') === topic.id),
    }))
    .filter((s) => s.terms.length > 0);
}
